/*
 * Copyright 2026 North Carolina State University
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { applyBrandTokens, DEFAULT_PRIMARY } from '../utils/brandColors';

/**
 * Loads the deployment's branding and applies it to the whole application.
 *
 * Runs OUTSIDE the auth boundary, because the sign-in page needs the logo, the
 * product name and the theme colour before anyone has logged in. The endpoint is
 * public for the same reason.
 *
 * Applying the theme is a matter of writing CSS custom properties onto
 * <html>; every stylesheet reads var(--brand-primary) and friends, so one write
 * re-themes everything, including components rendered before the fetch finished.
 */

/**
 * Where the logo and heading sit on the sign-in and sign-up pages, as
 * percentages of the header band. Matches the schema default on the server, and
 * covers a deployment whose settings predate the layout feature.
 */
export const DEFAULT_AUTH_LAYOUT = {
  logo: { x: 2, y: 12, width: 26 },
  heading: { x: 20, y: 62, width: 60 },
  heading_size: 2.4,
  heading_align: 'center'
};

/**
 * Where the API lives, for URLs the BROWSER fetches directly rather than axios.
 *
 * In production the API and the app share an origin, so this is empty and the
 * paths stay relative. In development they do not — the app is served from
 * :3000 and the API from :5050 — and a relative /api/branding/logo resolves
 * against :3000, where nothing answers. That is why an uploaded logo appeared
 * to do nothing locally: the upload worked, the image just could not be loaded
 * back.
 */
const apiBase = () => axios.defaults.baseURL || '';

const FALLBACK = {
  product_name: 'Phytotron Biospace',
  short_name: 'Phytotron',
  organization_name: '',
  primary_color: DEFAULT_PRIMARY,
  has_logo: false,
  logo_alt_text: '',
  has_favicon: false,
  signin_heading: '',
  signin_subheading: '',
  signup_heading: '',
  signup_subheading: '',
  sso_button_label: 'Sign in with Institution ID',
  support_email: '',
  footer_text: '',
  auth_layout: DEFAULT_AUTH_LAYOUT,
  version: 0
};

const BrandingContext = createContext({
  branding: FALLBACK,
  loading: true,
  refreshBranding: () => {},
  logoUrl: null
});

export const useBranding = () => useContext(BrandingContext);

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState(FALLBACK);
  const [loading, setLoading] = useState(true);

  const fetchBranding = useCallback(async () => {
    try {
      const { data } = await axios.get('/api/branding');
      setBranding({
        ...FALLBACK,
        ...data,
        // Merged rather than replaced: a settings document saved before the
        // layout existed returns nothing here, and half a layout would leave
        // the logo with no position at all.
        auth_layout: {
          ...DEFAULT_AUTH_LAYOUT,
          ...(data.auth_layout || {}),
          logo: { ...DEFAULT_AUTH_LAYOUT.logo, ...(data.auth_layout?.logo || {}) },
          heading: { ...DEFAULT_AUTH_LAYOUT.heading, ...(data.auth_layout?.heading || {}) }
        }
      });
    } catch (err) {
      // A branding failure must never keep people out of the app — fall back to
      // the built-in defaults and carry on.
      console.error('Could not load branding settings; using defaults.', err);
      setBranding(FALLBACK);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchBranding(); }, [fetchBranding]);

  // Paint the theme as early as possible, and again whenever it changes.
  useEffect(() => {
    applyBrandTokens(branding.primary_color);
  }, [branding.primary_color]);

  // Tab title and favicon follow the deployment's name.
  useEffect(() => {
    if (branding.product_name) {
      document.title = branding.product_name;
    }
    if (branding.has_favicon) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = `${apiBase()}/api/branding/favicon?v=${branding.version}`;
    }
  }, [branding.product_name, branding.has_favicon, branding.version]);

  const value = useMemo(() => ({
    branding,
    loading,
    refreshBranding: fetchBranding,
    // null when nothing has been uploaded, so callers can fall back to the
    // bundled logo. The version query busts the immutable cache on re-upload.
    logoUrl: branding.has_logo ? `${apiBase()}/api/branding/logo?v=${branding.version}` : null,
    // Always a usable description, even before an admin has set one.
    logoAlt: branding.logo_alt_text || `${branding.product_name} logo`
  }), [branding, loading, fetchBranding]);

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};

export default BrandingContext;
